// src/services/apaleo/reservations.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import i18n from "../../i18n";
import type { ReservationModel } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData, errorMessage, ALLOWED_PROPERTIES } from './config';

const part: ThisType<ApaleoClient> = {
async fetchReservations(
  from: string | null = null,
  to: string | null = null,
  dateFilterType = "Arrival",
  expand = "",
  textSearch = "",
  propertyId: string | null = null,
  maxPages: number = 50,
  options?: { onProgress?: (p: { page: number; rows: number }) => void },
): Promise<ReservationModel[]> {
  if (IS_DEV) {
    console.log("ApaleoAuth: Using Mock Reservations");
    return MockData.MOCK_RESERVATIONS;
  }
  if (ALLOWED_PROPERTIES.length === 0) return [];

  // Clamp the pageSize to the maximum allowed by Apaleo API (100) to prevent HTTP 422 and ensure pagination works correctly
  const activePageSize = 100;

  const fetchAllForProperty = async (propId: string): Promise<ReservationModel[]> => {
    let allReservations: ReservationModel[] = [];
    let pageNumber = 1;
    let hasMore = true;
    let safetyCounter = 0;

    while (hasMore && safetyCounter < maxPages) {
      // Safety limit: maxPages × 100 reservations per property (default 50 = 10,000)
      let urlParams = `pageSize=${activePageSize}&pageNumber=${pageNumber}`;
      if (expand) urlParams += `&expand=${expand}`;
      if (from && to) {
        const fromIso = from.includes("T") ? from : `${from}T00:00:00Z`;
        const toIso = to.includes("T") ? to : `${to}T23:59:59Z`;
        urlParams += `&dateFilter=${dateFilterType}&from=${fromIso}&to=${toIso}`;
      }
      if (textSearch) {
        urlParams += `&textSearch=${encodeURIComponent(textSearch)}`;
      }

      const data = (await this._fetch(
        `/booking/v1/reservations?propertyIds=${propId}&${urlParams}`,
      )) as { reservations?: ReservationModel[] } | null;

      if (data && data.reservations) {
        allReservations = [...allReservations, ...data.reservations];
        // Progress callback (per page, per property) — global FetchProgressBar.
        options?.onProgress?.({ page: pageNumber, rows: allReservations.length });
        // If we have fewer reservations than requested in activePageSize, we reached the end
        hasMore = data.reservations.length === activePageSize;
        pageNumber++;
      } else {
        options?.onProgress?.({ page: pageNumber, rows: allReservations.length });
        hasMore = false;
      }
      safetyCounter++;
    }
    return allReservations;
  };

  let targetProps: string[] = ALLOWED_PROPERTIES;
  if (propertyId && propertyId !== "All") {
    targetProps = ALLOWED_PROPERTIES.includes(propertyId) ? [propertyId] : [];
  }

  const promises = targetProps.map((id) => fetchAllForProperty(id));

  const results = await Promise.all(promises);
  return results
    .flat()
    .filter((res) => ALLOWED_PROPERTIES.includes(res.property?.id || ""));
},

async fetchReservationsAllTime(
  textSearch = "",
  propertyId: string | null = null,
  options?: { onProgress?: (p: { page: number; rows: number }) => void },
): Promise<ReservationModel[]> {
  if (IS_DEV) {
    console.log("ApaleoAuth: Using Mock Reservations (all-time search)");
    return MockData.MOCK_RESERVATIONS;
  }
  if (ALLOWED_PROPERTIES.length === 0) return [];

  // 90-day non-overlapping Stay windows. `to` is EXCLUSIVE per the Apaleo
  // contract, so [start, end) chunks never double-count boundary days.
  const CHUNK_DAYS = 90;
  const MAX_CHUNKS_BACKWARD = 20; // ~5 years of history
  const MAX_PAGES_PER_CHUNK = 10; // 10 × 100 = 1,000 rows per window

  const dayMs = 24 * 60 * 60 * 1000;
  // Local-date ISO formatting — .toISOString() would shift the boundary at
  // midnight CEST to the previous UTC day (AGENTS.md §2.2).
  const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T00:00:00Z`;

  let targetProps: string[] = ALLOWED_PROPERTIES;
  if (propertyId && propertyId !== "All") {
    targetProps = ALLOWED_PROPERTIES.includes(propertyId) ? [propertyId] : [];
  }

  const seen = new Set<string>();
  let cumulativeRows = 0;

  const fetchWindow = async (propId: string, fromIsoStr: string, toIsoStr: string): Promise<ReservationModel[]> => {
    const rows: ReservationModel[] = [];
    for (let pageNumber = 1; pageNumber <= MAX_PAGES_PER_CHUNK; pageNumber++) {
      const urlParams =
        `pageSize=100&pageNumber=${pageNumber}` +
        `&dateFilter=Stay&from=${fromIsoStr}&to=${toIsoStr}` +
        (textSearch ? `&textSearch=${encodeURIComponent(textSearch)}` : "");

      const data = (await this._fetch(
        `/booking/v1/reservations?propertyIds=${propId}&${urlParams}`,
      )) as { reservations?: ReservationModel[] } | null;

      if (data?.reservations?.length) {
        for (const res of data.reservations) {
          // `id` is optional on ReservationModel; a row without one cannot be
          // deduplicated or keyed downstream, so it is skipped rather than
          // letting `undefined` into the Set.
          if (!res.id) continue;
          if (!seen.has(res.id)) {
            seen.add(res.id);
            rows.push(res);
          }
        }
        cumulativeRows += data.reservations.length;
        options?.onProgress?.({ page: pageNumber, rows: cumulativeRows });
        if (data.reservations.length < 100) break; // window exhausted
      } else {
        options?.onProgress?.({ page: pageNumber, rows: cumulativeRows });
        break;
      }
    }
    return rows;
  };

  const fetchAllForProperty = async (propId: string): Promise<ReservationModel[]> => {
    const all: ReservationModel[] = [];
    const today = new Date();

    // Forward window (today .. today+90d) FIRST — catches upcoming
    // bookings before the historical walk.
    const forwardEnd = new Date(today.getTime() + CHUNK_DAYS * dayMs);
    all.push(...(await fetchWindow(propId, toIso(today), toIso(forwardEnd))));

    // Backward walk: chunk i covers [today - 90*(i+1), today - 90*i)
    for (let i = 0; i < MAX_CHUNKS_BACKWARD; i++) {
      const end = new Date(today.getTime() - i * CHUNK_DAYS * dayMs);
      const start = new Date(today.getTime() - (i + 1) * CHUNK_DAYS * dayMs);
      all.push(...(await fetchWindow(propId, toIso(start), toIso(end))));
    }
    return all;
  };

  const promises = targetProps.map((id) => fetchAllForProperty(id));
  const results = await Promise.all(promises);
  return results.flat();
},

async fetchReservationById(
  reservationId: string,
  expand = "timeSlices,totalAmount,totalGrossAmount,totalNetAmount",
): Promise<ReservationModel | null> {
  if (IS_DEV)
    return (
      MockData.MOCK_RESERVATIONS.find((r) => r.id === reservationId) ||
      MockData.MOCK_RESERVATIONS[0]
    );
  try {
    const qs = expand ? `?expand=${expand}` : "";
    return (await this._fetch(`/booking/v1/reservations/${reservationId}${qs}`)) as ReservationModel;
  } catch (e) {
    console.warn(`Fehler beim Laden der Reservierung ${reservationId}:`, e);
    return null;
  }
},

async updateReservation(
  reservationId: string,
  patchOperations: Record<string, unknown>[],
): Promise<unknown> {
  try {
    return await this._fetch(`/booking/v1/reservations/${reservationId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchOperations),
    });
  } catch (e) {
    throw new Error(
      `${i18n.t('common.error')}: ${reservationId}: ${errorMessage(e)}`,
    );
  }
},

async amendReservation(
  reservationId: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  if (!reservationId) throw new Error("amendReservation: reservationId is required");
  if (!body) throw new Error("amendReservation: body is required");
  return await this._fetch(
    `/booking/v1/reservation-actions/${reservationId}/amend`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
},

async checkInReservation(reservationId: string): Promise<unknown> {
  if (!reservationId) throw new Error("checkInReservation: reservationId is required");
  return await this._fetch(
    `/booking/v1/reservation-actions/${reservationId}/checkin`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
},

async checkOutReservation(reservationId: string): Promise<unknown> {
  if (!reservationId) throw new Error("checkOutReservation: reservationId is required");
  return await this._fetch(
    `/booking/v1/reservation-actions/${reservationId}/checkout`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
},

async cancelReservation(
  reservationId: string,
  reasonCode?: string,
  reason?: string,
): Promise<unknown> {
  if (!reservationId) throw new Error("cancelReservation: reservationId is required");
  const body: Record<string, unknown> = {};
  if (reasonCode) body.reasonCode = reasonCode;
  if (reason) body.reason = reason;
  return await this._fetch(
    `/booking/v1/reservation-actions/${reservationId}/cancel`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
},

async updateFolioCompany(
  folioId: string,
  companyData: { name?: unknown; address?: unknown; id?: unknown },
): Promise<unknown> {
  try {
    const payload = [
      { op: "remove", path: "/debitor/name" },
      { op: "remove", path: "/debitor/firstName" },
      { op: "replace", path: "/debitor/type", value: "Company" },
      {
        op: "add",
        path: "/debitor/company",
        value: { name: companyData.name },
      },
      { op: "add", path: "/debitor/address", value: companyData.address },
      { op: "add", path: "/company", value: { id: companyData.id } },
    ];
    return await this._fetch(`/finance/v1/folios/${folioId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      `${i18n.t('common.error')} ${folioId}: ${errorMessage(e)}`,
    );
  }
},

async fetchReservationsByBlock(blockId: string): Promise<ReservationModel[]> {
  if (IS_DEV) {
    return MockData.MOCK_RESERVATIONS;
  }
  try {
    const data = (await this._fetch(
      `/booking/v1/reservations?blockIds=${blockId}&pageSize=100`,
    )) as { reservations?: ReservationModel[] } | null;
    return data?.reservations || [];
  } catch (error) {
    console.error(
      `Fehler beim Laden der Reservierungen für Block ${blockId}:`,
      error,
    );
    return [];
  }
},

};

export default part;
